import { schemaAmount } from '@/src/lib/money';
import type { DayOfWeek, Menu, MenuItem, RestaurantSite } from '@/src/lib/hospitality/wire';

/**
 * The `Restaurant` → `hasMenu` → `MenuSection` → `MenuItem` graph, and the
 * `<script>` that carries it.
 *
 * <p>Brief 11.10a asks for this on the menu page, with an
 * `openingHoursSpecification` built from `RestaurantSiteDto.hours`. Every value
 * comes from the two DTOs; nothing is invented, and a field the operator has
 * not filled in is omitted rather than guessed — an empty `address` in a
 * knowledge panel is better than a wrong one.
 *
 * <p><strong>Prices go through `schemaAmount`, not through the display
 * formatter.</strong> Schema.org's `price` wants a plain decimal with no
 * grouping and no symbol; `formatMoney` produces `£12.50` or `12,50 ₺`, and
 * feeding either to a crawler is how a menu ends up listed at twelve pounds
 * fifty thousand.
 */

/** Schema.org spells its weekdays as URLs. */
const SCHEMA_DAY: Record<DayOfWeek, string> = {
  MONDAY: 'https://schema.org/Monday',
  TUESDAY: 'https://schema.org/Tuesday',
  WEDNESDAY: 'https://schema.org/Wednesday',
  THURSDAY: 'https://schema.org/Thursday',
  FRIDAY: 'https://schema.org/Friday',
  SATURDAY: 'https://schema.org/Saturday',
  SUNDAY: 'https://schema.org/Sunday',
};

/** `12:00:00` → `12:00`; schema.org wants `HH:mm` or `HH:mm:ss`, and the
 *  shorter one is what the operator typed. */
function clock(value: string | null): string | null {
  if (!value) return null;
  const match = /^(\d{1,2}):(\d{2})/.exec(value.trim());
  return match ? `${match[1].padStart(2, '0')}:${match[2]}` : null;
}

function menuItemNode(item: MenuItem, currency: string): Record<string, unknown> {
  const node: Record<string, unknown> = { '@type': 'MenuItem', name: item.title };
  if (item.description) node.description = item.description;
  if (item.mediaUrl) node.image = item.mediaUrl;
  const price = schemaAmount({ amount: item.price ?? '', currency });
  if (price && currency) {
    node.offers = {
      '@type': 'Offer',
      price,
      priceCurrency: currency,
      // The server's verdict, relayed. A dish that is off right now is still in
      // the graph — a menu that shrinks at 3pm reads to a crawler exactly as it
      // reads to a diner: like a different restaurant.
      availability: item.orderable ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
    };
  }
  return node;
}

/**
 * The whole `Restaurant` node for one site and one menu.
 *
 * <p>Exported separately from the component so the acceptance test can parse
 * what actually ships rather than a re-derivation of it.
 */
export function restaurantJsonLd(site: RestaurantSite, menu: Menu | null): Record<string, unknown> {
  const currency = menu?.currency || site.currency;

  const openingHours = site.hours
    .map((row) => {
      const opens = clock(row.opensAt);
      const closes = clock(row.closesAt);
      // A day with no times is a closed day. Schema.org expresses that with an
      // absent specification, not with `opens: null`.
      if (!opens || !closes) return null;
      return {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: SCHEMA_DAY[row.dayOfWeek],
        opens,
        closes,
      };
    })
    .filter((row) => row !== null);

  const node: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    name: site.name,
  };
  const street = site.addressText;
  if (street) {
    node.address = {
      '@type': 'PostalAddress',
      streetAddress: street,
      ...(site.locationName ? { name: site.locationName } : {}),
    };
  }
  if (currency) node.currenciesAccepted = currency;
  if (openingHours.length > 0) node.openingHoursSpecification = openingHours;
  if (menu) {
    node.hasMenu = {
      '@type': 'Menu',
      ...(site.locale ? { inLanguage: site.locale } : {}),
      hasMenuSection: menu.sections.map((section) => ({
        '@type': 'MenuSection',
        name: section.name,
        hasMenuItem: section.items.map((item) => menuItemNode(item, currency)),
      })),
    };
  }
  return node;
}

/**
 * The `application/ld+json` block itself.
 *
 * <p>`</` inside a string is escaped: a dish called `Fish "n" Chips </script>`
 * would otherwise close the tag and put the rest of the menu into the document
 * as markup. `JSON.stringify` does not do this for you.
 */
export function MenuJsonLd({ site, menu }: { site: RestaurantSite; menu: Menu | null }) {
  const json = JSON.stringify(restaurantJsonLd(site, menu)).replace(/<\//g, '<\\/');
  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: json }}
    />
  );
}
